/**
 * Preview generator utility functions
 */

export function generateInterviewPreview(formData, currentStep) {
  const {
    studentName, studentYear, studentTitle, studentPronouns,
    sourceName, sourceTitle, articleTopic, focusPoints,
    sourceConnection, interviewLength, availability, addContext,
  } = formData;

  let formattedFocusPoints = focusPoints || "[Focus Points]";
  if (focusPoints) {
    const points = focusPoints.split(",").map((p) => p.trim()).filter((p) => p);
    if (points.length === 2) {
      formattedFocusPoints = `${points[0]} and ${points[1]}`;
    } else if (points.length > 2) {
      const lastPoint = points.pop();
      formattedFocusPoints = `${points.join(", ")}, and ${lastPoint}`;
    } else {
      formattedFocusPoints = points[0] || "[Focus Points]";
    }
  }

  switch (currentStep) {
    case 1:
      return {
        title: "Your Information",
        content: (
          <div className="no-ribbon">
            <p>
              Hi, my name is {studentName || "[Your Name]"} and I am a{" "}
              {studentYear || "[Your Year]"}
              {addContext ? " at Woodside High School" : ""}. I&apos;m a part of{" "}
              {addContext ? "our student publication," : "our school newspaper,"}{" "}
              the Paw Print, and I am writing an article about{" "}
              {articleTopic || "[Article Topic]"}.
            </p>
            {(studentTitle || studentPronouns) && (
              <small className="preview-note">
                {studentTitle ? "Your title" : ""}
                {studentTitle && studentPronouns ? " and " : ""}
                {studentPronouns ? "your pronouns" : ""} will appear in the
                email signature.
              </small>
            )}
          </div>
        ),
      };
    case 2:
      return {
        title: "Source Information",
        content: (
          <>
            <p>
              I heard you are connected to {articleTopic || "[Article Topic]"}{" "}
              because you are the {sourceTitle || "[Source Title]"}.
            </p>
            <small className="preview-note">
              {sourceName ? "The source&apos;s name" : "Source name"} will appear in
              the email greeting.
            </small>
          </>
        ),
      };
    case 3:
      return {
        title: "Article Details",
        content: (
          <>
            <p>In this article, I want to explore {formattedFocusPoints}.</p>
            <small className="preview-note">
              {articleTopic ? "The article topic" : "Article topic"} appears in
              the email subject line and introduction.
            </small>
          </>
        ),
      };
    case 4:
      return {
        title: "Relevance &amp; Length",
        content: (
          <>
            <p>
              I heard you are connected to {articleTopic || "[Article Topic]"}{" "}
              because {sourceConnection || "[Source Connection]"} so I thought
              you would be a good person to talk to.
            </p>
            <p>
              Would you be open to a {interviewLength || "[Interview Length]"}{" "}
              interview?
            </p>
          </>
        ),
      };
    case 5:
      return {
        title: "Scheduling",
        content: (
          <>
            <p>
              If this sounds like something we could do, the best times for me
              to meet you would be {availability || "[Availability]"}. You would
              help support student journalism here at Woodside.
            </p>
            <p>Let me know what works for you!</p>
            <div className="email-signature">
              {studentName || "[Your Name]"}
              {studentTitle && <div>{studentTitle}</div>}
              {studentPronouns && <div>{studentPronouns}</div>}
            </div>
          </>
        ),
      };
    default:
      return null;
  }
}

export function generateQuoteCheckPreview(formData, currentStep) {
  const { qcStudentName, qcSourceName, quotes } = formData;

  switch (currentStep) {
    case 1:
      return {
        title: "Basic Information",
        content: (
          <>
            <p>Hi {qcSourceName || "[Source Name]"},</p>
            <p>
              Thanks again for doing the interview for my story. Here are the
              quotes I plan to use for it:
            </p>
            <small className="preview-note">
              {qcStudentName ? "Your name" : "Your name"} will appear in the
              email signature.
            </small>
          </>
        ),
      };
    case 2: {
      const quotesContent = [];
      quotes.forEach((quote) => {
        if (quote.text && quote.text.trim()) {
          if (quote.context && quote.context.trim()) {
            quotesContent.push(
              <p key={quote.id}>
                - {quote.context.trim()}, you said: &quot;{quote.text.trim()}&quot; - Does
                that sound right?
              </p>,
            );
          } else {
            quotesContent.push(
              <p key={quote.id}>
                - &quot;{quote.text.trim()}&quot; - Does that sound right?
              </p>,
            );
          }
        }
      });

      if (quotesContent.length === 0) {
        quotesContent.push(<p key="placeholder">[No quotes added yet]</p>);
      }

      return {
        title: "Quotes",
        content: (
          <>
            {quotesContent}
            <small className="preview-note">
              {qcSourceName ? "The source&apos;s name" : "Source name"} appears in
              the email greeting.
            </small>
          </>
        ),
      };
    }
    default:
      return null;
  }
}
